import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Cloudscape's drawer-triggers container has a hashed scoped class.
const CLOUDSCAPE_DRAWERS_SELECTOR = '[class*="universal-toolbar-drawers"]';

// If Cloudscape hasn't rendered a drawers container within this
// window we fall back to a fixed top-right overlay so the toggle
// never silently disappears on views without drawers.
//
// Chosen short enough that no-drawer views (default Legacy: agent
// assistant off, `tab !== 'launch'`) don't wait noticeably, and long
// enough that Cloudscape's widgetized toolbar has time to hydrate
// its drawer-triggers container before we conclude it's missing.
const FALLBACK_TIMEOUT_MS = 200;

// Position of the fallback overlay when no drawer-triggers container
// is found. Mirrors the pre-portal legacy overlay coordinates so the
// button lands in a familiar spot instead of somewhere arbitrary.
const FALLBACK_STYLE: React.CSSProperties = {
  position: "fixed",
  top: "62px",
  right: "60px",
  zIndex: 1001,
};

/**
 * Portals children next to Cloudscape's drawer triggers so they
 * inherit the toolbar's sticky/rubber-band behavior. Uses a dedicated
 * host `<div>` (opaque to Cloudscape's React tree) prepended into the
 * container. If the container isn't in the DOM at mount (Cloudscape's
 * toolbar loads via widgetization), we watch with a
 * `MutationObserver` and re-attach if the container appears or gets
 * re-created.
 *
 * Fallback: Cloudscape only renders `universal-toolbar-drawers` when
 * at least one drawer exists. On a view with an empty `drawers`
 * array (Loom Agent Assistant disabled and no submission-history
 * drawer, for example) the container never appears. After
 * `FALLBACK_TIMEOUT_MS` with no attach we render children as a fixed
 * top-right overlay so the toggle is always reachable. The timer
 * arms both on initial mount and whenever the host is detached after
 * being attached, so a tab switch that removes the drawers container
 * also flips back to the fallback.
 *
 * Consumed by both the V2 App Shell and the Legacy `<Content/>` so
 * the "Visit old/new view" toggle sits on the toolbar in both trees
 * instead of a hand-positioned fixed overlay.
 */
export const ToolbarPortal: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const [fallback, setFallback] = useState<boolean>(false);
  // Cleared by the effect on unmount but referenced by both the
  // observer and the timer callbacks — a ref avoids stale-closure
  // captures on `fallbackTimer`.
  const fallbackTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const ownedHost = document.createElement("div");
    ownedHost.dataset.testid = "toolbar-portal-host";
    ownedHost.style.display = "flex";
    ownedHost.style.alignItems = "center";
    // Prevent the flex parent from shrinking our text button to
    // icon-trigger width and wrapping the label.
    ownedHost.style.flexShrink = "0";
    ownedHost.style.whiteSpace = "nowrap";

    // Track the Cloudscape element we mutated + its prior style so we
    // can restore it on unmount instead of leaving state behind on a
    // parent we don't own.
    let styledContainer: HTMLElement | null = null;
    let prevJustifySelf = "";

    // Tracks whether `ownedHost` is currently prepended to a drawers
    // container. Used by the observer to detect the "was attached,
    // now detached" case — mutations that only *remove* nodes never
    // show up in `record.addedNodes`, so we can't rely on the
    // candidate filter alone to notice a detach.
    let wasAttached = false;

    const clearFallbackTimer = () => {
      if (fallbackTimerRef.current !== undefined) {
        window.clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = undefined;
      }
    };

    const armFallbackTimer = () => {
      if (fallbackTimerRef.current !== undefined) {
        return; // already armed
      }
      fallbackTimerRef.current = window.setTimeout(() => {
        fallbackTimerRef.current = undefined;
        if (!document.contains(ownedHost)) {
          setFallback(true);
        }
      }, FALLBACK_TIMEOUT_MS);
    };

    // Attach if the container is in the DOM. Returns true when the
    // container exists (whether or not we needed to re-attach).
    const findAndAttach = (): boolean => {
      const container = document.querySelector(CLOUDSCAPE_DRAWERS_SELECTOR);
      if (!container) {
        return false;
      }
      if (ownedHost.parentElement !== container) {
        container.prepend(ownedHost);
      }
      // Call setHost unconditionally so React reconciles state even
      // when the container node is unchanged (e.g. Cloudscape
      // detached and re-inserted the same node with `ownedHost` still
      // parented to it). React skips the re-render when the value is
      // identical, so this is free on the steady-state path.
      setHost(ownedHost);
      wasAttached = true;
      // We're on the toolbar again — cancel any pending fallback and
      // clear a stale fallback flag if the previous state had us in
      // overlay mode.
      clearFallbackTimer();
      setFallback(false);
      // `justify-self: end` on the drawers grid item pulls the
      // [toggle, AI trigger] cluster tight against the right edge.
      // Re-apply on every attach so a re-mounted container also
      // gets it; restore the previous container's style first so we
      // don't leave a stale mutation on a detached node.
      if (container instanceof HTMLElement && styledContainer !== container) {
        if (styledContainer !== null) {
          styledContainer.style.justifySelf = prevJustifySelf;
        }
        styledContainer = container;
        prevJustifySelf = container.style.justifySelf;
        container.style.justifySelf = "end";
      }
      return true;
    };

    // Observe the whole body subtree for the app's lifetime so we
    // can re-attach if Cloudscape ever re-creates the drawers
    // container (`isLoomAgentAssistantEnabled` resolves async, and
    // route/`contentType` changes can prompt Cloudscape to re-render
    // the toolbar).
    //
    // Three hot-path guards keep this cheap even on table-heavy
    // views where the observer fires on every row render or
    // virtual-scroll update:
    //  1. When `ownedHost` is already attached, `document.contains`
    //     short-circuits before we do any per-record work.
    //  2. Otherwise we filter added nodes with a shallow
    //     `Element.matches` check. Cloudscape's widgetized toolbar
    //     inserts the drawers container as a top-level added node,
    //     so we intentionally skip the descendant `querySelector`
    //     that would traverse large virtualized-table subtrees.
    //  3. Actual attach work is coalesced through
    //     `requestAnimationFrame`, so bursts of mutations within a
    //     single frame produce at most one `findAndAttach` call.
    const hasDrawersCandidate = (records: MutationRecord[]): boolean => {
      for (const record of records) {
        for (let i = 0; i < record.addedNodes.length; i++) {
          const node = record.addedNodes[i];
          if (
            node instanceof Element &&
            node.matches(CLOUDSCAPE_DRAWERS_SELECTOR)
          ) {
            return true;
          }
        }
      }
      return false;
    };

    let scheduledRafId: number | undefined;
    const scheduleAttachCheck = () => {
      if (scheduledRafId !== undefined) return;
      scheduledRafId = window.requestAnimationFrame(() => {
        scheduledRafId = undefined;
        if (document.contains(ownedHost)) return;
        if (!findAndAttach()) {
          if (wasAttached) {
            wasAttached = false;
            setHost(null);
            // Detach from the (already-detached) previous container
            // so that if Cloudscape later re-inserts the *same*
            // container node it produces a fresh "added" mutation
            // and this observer can attach cleanly. Without this,
            // `ownedHost` stays parented to the detached node,
            // `document.contains` flips back to true on re-insert,
            // and the fast-path early return keeps `host` stuck at
            // null.
            ownedHost.remove();
          }
          armFallbackTimer();
        }
      });
    };

    const attachedImmediately = findAndAttach();
    const observer = new MutationObserver((records) => {
      if (document.contains(ownedHost)) {
        return;
      }
      // Two paths worth acting on:
      //  - We were previously attached and got detached (Cloudscape
      //    tore the drawers container down). The added-node filter
      //    can't see removal-only records, so we detect it here.
      //  - The mutation added a drawers container to attach to.
      // Anything else (table rows, virtual-scroll churn) is skipped
      // without touching the DOM.
      if (!wasAttached && !hasDrawersCandidate(records)) {
        return;
      }
      scheduleAttachCheck();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Arm the fallback timer if we didn't attach synchronously. If
    // the container appears later the observer clears it.
    if (!attachedImmediately) {
      armFallbackTimer();
    }

    return () => {
      observer.disconnect();
      clearFallbackTimer();
      if (scheduledRafId !== undefined) {
        window.cancelAnimationFrame(scheduledRafId);
      }
      if (styledContainer !== null) {
        styledContainer.style.justifySelf = prevJustifySelf;
      }
      ownedHost.remove();
    };
  }, []);

  if (host !== null) {
    return createPortal(children, host);
  }
  if (fallback) {
    return <div style={FALLBACK_STYLE}>{children}</div>;
  }
  return null;
};

export default ToolbarPortal;
